import type { ReservationMode } from '@iheartjane/types';
import { useMemo } from 'react';

import { selectCart } from '@jane/monolith/customer/redux/cart';
import { useCustomerSelector } from '@jane/monolith/customer/selectors';
import { generateCartLimitReport } from '@jane/monolith/utils/cartLimitPolicy';
import type { CartLimitPolicyReport } from '@jane/monolith/utils/cartLimitPolicy';
import { minimumDeliveryOrder } from '@jane/monolith/utils/minimumDeliveryOrder';
import { minimumPickupOrder } from '@jane/monolith/utils/minimumPickupOrder';
/* eslint-disable-next-line no-restricted-imports */
import type { Store } from '@jane/shared/models';
import type { CartLineItems, DeliveryValidation } from '@jane/shared/types';

export interface UseValidCartProps {
  cartLineItems: CartLineItems;
  deliveryValidationData: DeliveryValidation;
  /**
   * The cart's mode. `'pickup'` and `'curbside'` both take the pickup minimum,
   * which `getPickupMinAmount` resolves from the store's curbside setting.
   */
  reservationMode: ReservationMode;
  store: Store;
}

interface UseValidCartReturn {
  cartReport: CartLimitPolicyReport | null;
  hasValidCart: boolean;
  minimumDeliveryOrderMet: boolean;
  minimumPickupOrderMet: boolean;
}

export const useValidateCart = ({
  cartLineItems,
  deliveryValidationData,
  reservationMode: currentReservationMode,
  store,
}: UseValidCartProps): UseValidCartReturn => {
  const {
    cart,
    isLoading: isLoadingCart,
    salesTaxLoading,
  } = useCustomerSelector(selectCart);

  return useMemo(() => {
    const earlyReturn = {
      cartReport: null,
      hasValidCart: false,
      minimumDeliveryOrderMet: false,
      minimumPickupOrderMet: false,
    };

    const cartReport =
      store && store.cart_limit_policy
        ? generateCartLimitReport({
            cart,
            store,
          })
        : null;

    if (isLoadingCart || salesTaxLoading) {
      return earlyReturn;
    }

    const underCartLimit = cartReport ? !cartReport.over : true;

    const minimumPickupOrderMet =
      currentReservationMode === 'pickup' ||
      currentReservationMode === 'curbside'
        ? minimumPickupOrder(cartLineItems.discountedSubtotal, store)
        : true;

    const minimumDeliveryOrderMet =
      deliveryValidationData && currentReservationMode === 'delivery'
        ? minimumDeliveryOrder({
            discountedSubtotal: cartLineItems.discountedSubtotal,
            store,
            deliveryValidateData: deliveryValidationData,
          }) && !!deliveryValidationData?.can_deliver
        : true;

    const hasInvalidAddress =
      currentReservationMode === 'delivery'
        ? !deliveryValidationData?.can_deliver
        : false;

    const hasValidCart =
      underCartLimit &&
      minimumPickupOrderMet &&
      minimumDeliveryOrderMet &&
      !hasInvalidAddress;

    return {
      cartReport,
      hasValidCart,
      minimumPickupOrderMet,
      minimumDeliveryOrderMet,
    };
  }, [
    cart,
    cartLineItems.discountedSubtotal,
    currentReservationMode,
    deliveryValidationData,
    isLoadingCart,
    salesTaxLoading,
    store,
  ]);
};
